import React from 'react';
import type { ComponentProps } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type {
  AlignItemsProps,
  BackgroundColorProps,
  BorderColorProps,
  BorderProps,
  BorderRadiusProps,
  BorderStyleProps,
  BorderWidthProps,
  DisplayProps,
  SpaceProps,
} from 'styled-system';
import {
  alignItems,
  backgroundColor,
  border,
  borderColor,
  borderRadius,
  borderStyle,
  borderWidth,
  display,
  space,
  themeGet,
} from 'styled-system';

import { getForwardPropOpts } from '../theme/helpers/forward-props';
import { boxSizing, type BoxSizingProps } from '../theme/system-utilities';

interface CardFlags {
  selected?: boolean;
  disabled?: boolean;
}

const containerHoverCSS = (props: CardFlags) => css`
  ${!props.disabled &&
  !props.selected &&
  `
    border-color: ${themeGet('colors.sys.neutral.border.default')(props)};

    svg {
      color: ${themeGet('colors.sys.neutral.background.hover')(props)};
    }
  `};
`;

const containerFocusCSS = (props: CardFlags) => css`
  outline: 1px solid ${themeGet('colors.sys.secondary.border.default')(props)};
  border-color: ${themeGet('colors.sys.secondary.border.default')(props)};
  border-width: 1px;
`;

const containerDisabledCSS = (props: CardFlags) => css`
  background-color: ${themeGet('colors.sys.neutral.background.weak')(props)};
  border-color: ${themeGet('colors.sys.neutral.border.default')(props)};

  span {
    border-color: ${themeGet('colors.sys.neutral.border.active')(props)};
    color: ${themeGet('colors.sys.neutral.text.default')(props)};
  }
`;

const containerEnabledCSS = () => css`
  cursor: pointer;
`;

export const containerCheckedCSS = (props: CardFlags) => css`
  cursor: default;
  outline: 1px solid ${themeGet('colors.sys.primary.border.default')(props)};
  border-color: ${themeGet('colors.sys.primary.border.default')(props)};
  border-width: 1px;
`;

const UnstyledSelectCard = styled('div', getForwardPropOpts(['display', 'selected', 'disabled']))<
  DisplayProps &
    SpaceProps &
    BackgroundColorProps &
    BorderProps &
    BorderRadiusProps &
    BorderWidthProps &
    BorderStyleProps &
    BorderColorProps &
    BoxSizingProps &
    AlignItemsProps &
    CardFlags
>`
  ${display};
  ${alignItems};
  ${space};
  ${backgroundColor};
  ${border};
  ${borderRadius};
  ${borderWidth};
  ${borderStyle};
  ${borderColor};
  ${boxSizing};

  ${({ disabled }) => (disabled ? containerDisabledCSS : containerEnabledCSS)};

  ${({ selected }) => selected && containerCheckedCSS};

  &:hover {
    ${containerHoverCSS};
  }

  &:focus-within {
    ${containerFocusCSS};
  }

  &:last-child {
    margin-bottom: ${themeGet('space.sp_0')};
  }
`;

type SelectCardProps = ComponentProps<typeof UnstyledSelectCard> & {
  ref?: React.Ref<HTMLDivElement>;
};

const SelectCard = ({ ref, ...props }: SelectCardProps) => (
  <UnstyledSelectCard
    ref={ref}
    display="block"
    borderRadius="medium"
    borderWidth={1}
    borderStyle="solid"
    borderColor="sys.neutral.border.weak"
    backgroundColor="sys.neutral.background.default"
    pl="sp_12"
    pr="sp_16"
    py="sp_20"
    mb="sp_8"
    boxSizing="border-box"
    {...props}
  />
);

SelectCard.displayName = 'SelectCard';

export default SelectCard;
